import AuthGuard from '../components/AuthGuard';
import CardFundPromotionModal from '../components/CardFundPromotionModal';
import CardFundPromotionModalV2 from '../components/CardFundPromotionModalV2';
import CardFundPromotionModalV3 from '../components/CardFundPromotionModalV3';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

export default function SellPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const walletType = searchParams.get('type') || 'dangzheng';
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [bound, setBound] = useState(false);
  const [sellOpen, setSellOpen] = useState(true);
  const [canSell, setCanSell] = useState(false);
  const [bank, setBank] = useState<{ name?: string; number?: string; type?: string } | null>(null);
  const [alipayBound, setAlipayBound] = useState(false);
  const [alipay, setAlipay] = useState<{ name?: string; account?: string } | null>(null);
  const [withdrawMethod, setWithdrawMethod] = useState<'bank' | 'alipay'>('bank');
  const [settings, setSettings] = useState<{ 
    enabled: Record<string, boolean>; 
    start_date: Record<string, string | null>; 
    min: Record<string, number>; 
    fee_bps: Record<string, number>; 
    daily_withdrawal_count_limit: number; 
    count_today: number;
  } | null>(null);
  const [totalDama, setTotalDama] = useState(0);
  const [damaCheckEnabled, setDamaCheckEnabled] = useState(false);
  const [showDama, setShowDama] = useState(false);
  const [showDamaModal, setShowDamaModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [showBalance, setShowBalance] = useState(true);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [buzhuActivated, setBuzhuActivated] = useState<boolean | null>(null);
  const [showPayChannelModal, setShowPayChannelModal] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [dangzhengWithdrawDisabled, setDangzhengWithdrawDisabled] = useState(false);
  const [dangzhengWithdrawMessage, setDangzhengWithdrawMessage] = useState('');
  const [pendingPrompt, setPendingPrompt] = useState(false);
  const [pendingPayUrl, setPendingPayUrl] = useState<string | null>(null);
  const [countdownHours, setCountdownHours] = useState<number | null>(null);
  const [isV2, setIsV2] = useState(false);
  const [showFundRequireModal, setShowFundRequireModal] = useState(false);
  const [showFundPromoModal, setShowFundPromoModal] = useState(false);
  const [hasFundPromo, setHasFundPromo] = useState(true);
  const [hasFundPurchaseSinceCutoff, setHasFundPurchaseSinceCutoff] = useState(true);
  const [showPriorityWithdrawModal, setShowPriorityWithdrawModal] = useState(false);
  const [showDangTypeModal, setShowDangTypeModal] = useState(false);
  const [dangTypeModalMessage, setDangTypeModalMessage] = useState('请先开通党员之家');
  const [priorityPayLoading, setPriorityPayLoading] = useState(false);
  const cardPasswordSetupEnabled = (import.meta as any).env?.VITE_CARD_PASSWORD_SETUP_ENABLED === 'true';
  const isDev = import.meta.env.VITE_IS_DEV === 'true';

  useEffect(() => {
    api.get('/me').then(({ data }) => {
      const acc = data?.accounts ?? {};
      setBalance(acc[walletType] ?? 0);
      setCanSell(!!data?.can_sell);
      setTotalDama(Number(data?.total_dama ?? 0));
      setShowDama(!!data?.show_dama);
      setUserName(data?.name ?? '');
    }).catch(() => {});
    api.get('/bank/status').then(({ data }) => {
      const info = data?.data ?? {};
      setBound(!!data?.bound);
      setBank({ name: info?.bank ?? undefined, number: info?.card ?? undefined, type: info?.name ?? undefined });
    }).catch(() => { setBound(false); setBank(null); });
    api.get('/alipay/status').then(({ data }) => {
      setAlipayBound(!!data?.bound);
      if (data?.data) setAlipay({ name: data.data.name ?? undefined, account: data.data.account ?? undefined });
    }).catch(() => { setAlipayBound(false); setAlipay(null); });
    api.get(isDev ? '/test/config/features' : '/config/features').then(({ data }) => {
      const key = `sell_status_${walletType}`;
      setSellOpen(data?.[key] !== false);
      setDamaCheckEnabled(!!data?.dama_check_enabled);
    }).catch(() => setSellOpen(true));
    api.get(isDev ? '/test/withdraw/settings' : '/withdraw/settings').then(({ data }) => {
      const enabled = data?.enabled ?? {};
      const startDate = data?.start_date ?? {};
      const min: Record<string, number> = {};
      const feeBps: Record<string, number> = {};
      for (const k of Object.keys(data?.min ?? {})) min[k] = Number(data.min[k] ?? 0);
      for (const k of Object.keys(data?.fee_bps ?? {})) feeBps[k] = Number(data.fee_bps[k] ?? 0);
      setSettings({
        enabled, start_date: startDate, min, fee_bps: feeBps,
        daily_withdrawal_count_limit: Number(data?.daily_withdrawal_count_limit ?? 0),
        count_today: Number(data?.count_today ?? 0),
      });
    }).catch(() => setSettings(null));
    api.get('/sell/history').then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      let pending = 0, withdrawn = 0;
      list.forEach((item: any) => {
        const amt = Number(item.amount ?? 0);
        if (item.status === 'pending') pending += amt;
        if (item.status === 'approved' || item.status === 'completed') withdrawn += amt;
      });
      setPendingAmount(pending);
      setTotalWithdrawn(withdrawn);
    }).catch(() => {}); 
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);
  const handleInputTouch = useCallback((e: React.TouchEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    if (el && !el.disabled) setTimeout(() => el.focus(), 0);
  }, []);
  
  async function submit() {
    if (submitting) return;
 
    const walletEnabled = settings?.enabled?.[walletType] ?? true;
    if (!(sellOpen && walletEnabled)) {
      toast.error(settings?.start_date?.[walletType] ? `${settings.start_date[walletType]} 开放提现` : '暂未开放');
      return;
    }
    if (damaCheckEnabled && totalDama > 0) { setShowDamaModal(true); return; }
    const lim = Number(settings?.daily_withdrawal_count_limit || 0);
    const cnt = Number(settings?.count_today || 0);
    if (lim > 0 && cnt >= lim) { toast.error(`今日提现次数已达上限（${cnt}/${lim}）`); return; }
    if (!canSell) { toast.error('无权限，请联系管理员'); return; }
    if (withdrawMethod === 'alipay' && !alipayBound) { toast.error('请先绑定支付宝'); return; }
    if (withdrawMethod === 'bank' && !bound) { toast.error('请先绑定银行卡'); return; }
    if (!amount) { toast.error('请输入提现金额'); return; }
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) { toast.error('金额格式不正确'); return; }
    const min = Number(settings?.min?.[walletType] || 0);
    if (min > 0 && num < min) { toast.error(`最低提现金额为 ¥${min}`); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(isDev ? '/test/sell' : '/sell', { wallet: walletType, amount: num, withdraw_method: withdrawMethod });
      toast.success(data?.message ?? '已提交提现');
      setAmount('');
      if (typeof window !== 'undefined') window.location.assign(`/wallet?type=${walletType}`);
    } catch (err: any) {
      if (err?.response?.data?.code === 'dang_tier_required') { setDangTypeModalMessage(err.response.data.message); setShowDangTypeModal(true); }
      else toast.error(err?.response?.data?.message ?? '提交失败');
    } finally { setSubmitting(false); }
  }

  const feeBps = Number(settings?.fee_bps?.[walletType] || 0);
  const amtNum = Number(amount || '0');
  const estFee = useMemo(() => (!Number.isFinite(amtNum) || amtNum <= 0) ? 0 : Number(((amtNum * feeBps) / 100).toFixed(2)), [amtNum, feeBps]);
  const estNet = useMemo(() => (!Number.isFinite(amtNum) || amtNum <= 0) ? 0 : Number((amtNum - estFee).toFixed(2)), [amtNum, estFee]);
  const fmt = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const methodBound = withdrawMethod === 'alipay' ? alipayBound : bound;
  const isDisabled = submitting || !sellOpen || !canSell || !methodBound || !amount || amtNum > balance;

  return (
    <div className="max-w-md mx-auto bg-[url(/images/sell_beijing.png)] bg-[length:100%_100%] bg-no-repeat bg-top min-h-screen pb-[100px] pt-[150px] pb-[130px] relative">
      <AuthGuard />

      {/* Hero header */}
      <div className="absolute top-0 left-0">
        <div className="absolute top-0 left-0 right-0 px-4 pt-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button> 
          </div>
        </div>
      </div>

      {/* Balance card */}
      <div className="px-4 mb-[10px] relative z-10">
        <div className="rounded-xl p-4">
          <div className="flex items-start justify-between pl-[70px]">
            <div className="flex items-center gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[#5a2d0c] text-xs">可提现金额（元）</span>
                  <button onClick={() => setShowBalance(!showBalance)}>
                    <img src="/images/sell_yanjing.png" alt="" className="w-4 h-3" style={{ opacity: showBalance ? 1 : 0.4 }} />
                  </button>
                </div>
                <p className="text-[#8b0000] text-3xl font-bold mt-1">{showBalance ? fmt(balance) : '****'}</p>
              </div>
            </div>
            <button onClick={() => navigate('/sell/history')} className="bg-[#c4161c] text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1 flex-shrink-0">
              提现记录 <img src="/images/sell_jiantou.png" alt="" className="w-2.5 h-2.5" />
            </button>
          </div>
          <div className="flex mt-1 bg-[url(/images/sell_kuang.png)] bg-no-repeat bg-[length:100%_100%] p-3">
            <div className="flex-1">
              <p className="text-[#8b5e3c] text-[10px]">提现申请中（元）</p>
              <p className="text-[#5a2d0c] font-bold text-sm mt-0.5">{fmt(pendingAmount)}</p>
            </div>
            <div className="w-px bg-[#e8c870]/40" />
            <div className="flex-1 pl-4">
              <p className="text-[#8b5e3c] text-[10px]">累计已提现（元）</p>
              <p className="text-[#5a2d0c] font-bold text-sm mt-0.5">{fmt(totalWithdrawn)}</p>
            </div>
          </div>
        </div>
      </div>
  
      <div className="px-4 space-y-2">
        {/* Not open overlay */}
        {!sellOpen && (
          <div className="p-6 rounded-xl bg-white/90 text-center">
            <div className="text-4xl mb-3">🕐</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">暂未开放</h3>
            <p className="text-[#e62129] font-bold text-[15px]">请留意官方群内通知提现通道开放时间</p>
          </div>
        )}

        {/* 真实姓名 row */}
        <div className="bg-white rounded-xl px-4 py-2 flex items-center gap-3">
          <img src="/images/sell_zhenshi.png" alt="" className="w-7 h-7 flex-shrink-0" />
          <span className="text-[#333] text-sm font-medium">真实姓名</span>
          <span className="flex-1 text-right text-[#333] text-sm">{userName || '---'}</span>
          <img src="/images/sell_jiantou.png" alt="" className="w-2.5 h-2.5 opacity-30" />
        </div>

        {/* 当前余额 row */}
        <div className="bg-white rounded-xl px-4 py-2 flex items-center gap-3">
          <img src="/images/sell_ing.png" alt="" className="w-7 h-7 flex-shrink-0" />
          <span className="text-[#333] text-sm font-medium">当前余额</span>
          <span className="flex-1 text-right text-[#333] text-sm">{fmt(balance)}</span>
          <img src="/images/sell_jiantou.png" alt="" className="w-2.5 h-2.5 opacity-30" />
        </div>

        {/* 提现金额 section */}
        <div className="bg-white rounded-xl px-4 py-2">
          <div className="flex items-center gap-2 mb-1">
            <img src="/images/sell_tixian.png" alt="" className="w-7 h-7" />
            <span className="text-[#333] text-sm font-bold">提现金额</span>
          </div>

          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <span className="text-[#c4161c] font-bold text-[11px]">¥</span>
              <span className="text-[#666] text-[11px]">最低提现金额 {Number(settings?.min?.[walletType] || 0)}元</span>
            </div>
            <button type="button" className="text-[#c4161c] text-[11px] font-bold" onClick={() => setAmount(String(Math.floor(balance || 0)))}>全部提现</button>
          </div>

          <input
            ref={inputRef}
            className="w-full bg-[#f5f5f5] rounded-md text-md px-4 py-1 text-[#333] focus:outline-none focus:ring-2 focus:ring-[#c4161c]/20 mb-1"
            inputMode="decimal"
            placeholder="请输入提现金额"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            onTouchEnd={handleInputTouch}
          />
          {amtNum > balance && <div className="mb-2 text-[12px] text-[#c4161c]">提现金额不能超过余额 ¥{fmt(balance)}</div>}

          <div className="grid grid-cols-5 gap-1">
            {[500, 800, 1000, 1500, 2000, 2500, 3000, 4000, 4500, 5000].map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setAmount(String(v))}
                disabled={v > balance}
                className={`py-1 text-[11px] rounded-lg font-medium transition ${
                  Number(amount) === v
                    ? 'bg-[#c4161c] text-white'
                    : v > balance
                      ? 'bg-[#f5f5f5] text-[#ccc]'
                      : 'bg-[#f5f5f5] text-[#333] border border-[#eee]'
                }`}
              >
                ¥{v}
              </button>
            ))}
          </div>

          {feeBps > 0 && <div className="mt-2 text-[11px] text-[#999]">手续费：{feeBps}%（约 ¥{estFee}），预计到账：¥{estNet}</div>}
        </div>

        {/* 提现至 section */}
        <div className="bg-white rounded-xl px-4 py-2">
          <div className="flex items-center gap-3">
            <img src="/images/sell_txianzhi.png" alt="" className="w-7 h-7 flex-shrink-0" />
            <span className="text-[#333] text-sm font-bold">提现至</span>
            <div className="flex-1 text-right">
              {bound && bank?.number ? (
                <span className="text-[#333] text-xs">{bank.name} ***{(bank.number ?? '').slice(-4)}</span>
              ) : (
                <a href="/bind" className="text-[#c4161c] text-xs">请选择提现账户 &gt;</a>
              )}
            </div>
            <img src="/images/sell_jiantou.png" alt="" className="w-2.5 h-2.5 opacity-30" />
          </div>
        </div>

        {sellOpen && !canSell && <div className="text-center text-[12px] text-white/60">无权限，需管理员开通</div>}
        {(() => { const l = Number(settings?.daily_withdrawal_count_limit || 0); const c = Number(settings?.count_today || 0); return l > 0 ? <div className="text-center text-[12px] text-white/60">今日已提现 {c}/{l} 次</div> : null; })()}


        {damaCheckEnabled && (showDama || totalDama > 0) &&  (
          <div className="mt-4 text-center text-[12px] text-white p-3 bg-[#ff8329] rounded-xl">剩余打码量：{totalDama}</div>
        )}
            
        {/* Submit button */}
        <button onClick={submit} disabled={isDisabled} className="w-[330px] mx-auto flex items-center justify-center mt-4">
          <img src="/images/sell_queren.png" alt="确认提现" className={`w-full ${isDisabled ? 'opacity-80' : ''}`} />
        </button>

      </div>
  
      {/* === Modals === */}
      {showDamaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-[280px] rounded-2xl bg-white p-4 text-center shadow relative">
            <button onClick={() => setShowDamaModal(false)} className="absolute right-2 top-2 text-sm text-gray-400">&times;</button>
            <div className="my-5 text-[12px] text-red-600">打码量未清除，请联系办理人员清除</div>
            <button onClick={() => setShowDamaModal(false)} className="w-full rounded-lg bg-red-600 py-1 text-xs text-white">知道了</button>
          </div>
        </div>
      )}
    </div>
  );
}
